// MQTT traffic monitor — the data behind the Super Admin message console.
//
// The app normally subscribes to exactly the devices on screen. The console
// needs to see EVERYTHING, so it adds a wildcard subscription while it is open
// and drops it on the way out. Nothing else changes: the same client, the same
// connection, the same per-device subscriptions carry on underneath.
//
// SUBSCRIBES TO '#', like the reference console.
//
// An earlier version used `+/client` and `+/device`, reasoning that those are
// the only shapes devices use and a narrower filter keeps the volume down. That
// was a guess about the topic space, and a wrong guess silently DROPS messages —
// which is the one thing a diagnostic tool must never do. If a device ever
// publishes to a third shape, '#' shows it and the narrow filter would not.
//
// MEMORY: a ring buffer, newest first, capped at MAX_MESSAGES.
//
// RENDER RATE: listeners are notified on a timer, not per message. A busy broker
// delivers faster than React can re-render a list, and one setState per message
// makes the view lag behind the traffic — which reads as "messages are missing"
// even though the buffer has them all.

import {
  connectMqtt, addMessageListener, subscribeTopic, unsubscribeTopic,
} from './mqttClient';
import { isSubscribed } from './consoleSubs';

export const MAX_MESSAGES = 500;

/*
 * ⚠️ THE FILTERED PANE NEEDS ITS OWN RING. It used to be a `uno` filter over
 * `buffer` alone — and `buffer` holds the last 500 lines of '#'. On a busy
 * broker that turns over in seconds, so a watched device's reply appeared in
 * Filtered and then VANISHED as it fell off the end of the shared ring: "data
 * is seen and immediately cleared". Watched lines are now ALSO kept here,
 * capped on their own, so only other watched traffic can push them out.
 */
export const MAX_WATCHED = 500;

// Everything on the broker — see the note above on why this is not narrowed.
const WILDCARDS = ['#'];

// How often the UI is told about new messages. 120 ms keeps the list feeling
// live while collapsing a burst of twenty messages into ONE re-render instead of
// twenty.
const FLUSH_MS = 120;

let buffer = [];          // newest first
let watched = [];         // newest first — only lines for subscribed unos
let seq = 0;
const listeners = new Set();
let detach = null;
let refCount = 0;
let flushTimer = null;
let dirty = false;

function emit() {
  dirty = false;
  listeners.forEach((fn) => { try { fn(buffer, watched); } catch (e) { /* ignore */ } });
}

// Coalesce notifications. The first message after an idle period paints
// immediately (so a single command's reply is not delayed); a burst then flushes
// on the timer.
function notify() {
  if (flushTimer) { dirty = true; return; }
  emit();
  flushTimer = setInterval(() => {
    if (dirty) { emit(); return; }
    clearInterval(flushTimer);
    flushTimer = null;
  }, FLUSH_MS);
}

// "897242/client" -> "897242"
export function unoFromTopic(topic) {
  const t = String(topic || '');
  const slash = t.indexOf('/');
  return slash === -1 ? t : t.slice(0, slash);
}

/*
 * Which way a message went. Reported once here so both consoles draw it the
 * same, and drawn FIRST on the line, because of this:
 *
 *     13:34:24  7389692/client: [7389692]1-0#1
 *     13:34:24  7389692/device: [7389692]1-0#1
 *
 * ⚠️ THAT IS NOT A DUPLICATE, AND IT WAS REPORTED AS ONE. The second line is the
 * command the app SENT (`<uno>/device`); the first is the status the device
 * REPORTED back (`<uno>/client`) — and for a one-gang switch the two formats
 * are textually identical, so the pair reads as the same message twice. A
 * second MQTT tool "showed it once" only because it subscribes to `+/client`
 * and never sees the command at all. This console watches `#` on purpose
 * (see the top of this file), so it sees both — which is the useful thing,
 * command and echo with the round-trip time between them, but only if the eye
 * can tell them apart from the first character.
 *
 * Verified before deciding this: the broker delivers ONE copy for overlapping
 * subscriptions ('#' plus an exact topic — tested live), the client emits one
 * `message` event per packet, and the monitor holds one ref-counted listener.
 * Nothing in the pipeline doubles a message.
 */
export const DIR_TO_DEVICE = 'out';   // <uno>/device — a command going out
export const DIR_FROM_DEVICE = 'in';  // <uno>/client — a status coming back
export const DIR_APP = 'app';         // something this console itself did

export function directionOf(topic) {
  const t = String(topic || '');
  if (t === 'app') return DIR_APP;
  if (t.endsWith('/device')) return DIR_TO_DEVICE;
  if (t.endsWith('/client')) return DIR_FROM_DEVICE;
  return '';
}

/** The same mark as text, for a copied log — so a paste reads like the screen. */
export const directionGlyph = (dir) => (
  dir === DIR_TO_DEVICE ? '→' : dir === DIR_FROM_DEVICE ? '←' : dir === DIR_APP ? '·' : ' '
);

/**
 * A line as SHOWN: `<uno>/client: [<uno>]RfV(…)` names the device twice, so the
 * `[<uno>]` echo is dropped when it repeats the topic (owner, 2026-10-15: "all
 * message should be once"). A payload addressed to a DIFFERENT number keeps it.
 * Search still runs on the untouched `text`.
 */
export function displayText(m) {
  const uno = String(m?.uno || '');
  const text = String(m?.text || '');
  if (!uno || uno === 'app') return text;
  const echo = `: [${uno}]`;
  const at = text.indexOf(echo);
  return at >= 0 && at === String(m.topic || '').length ? `${text.slice(0, at)}: ${text.slice(at + echo.length)}` : text;
}

function record(topic, payload) {
  const now = new Date();
  seq += 1;
  const entry = {
    id: seq,
    at: now,
    // HH:MM:SS, matching the console's log format.
    time: now.toTimeString().slice(0, 8),
    topic,
    uno: unoFromTopic(topic),
    dir: directionOf(topic),
    // `<topic>: <payload>` is how a line reads in the reference console.
    text: `${topic}: ${payload}`,
    payload,
  };
  buffer = [entry, ...buffer];
  if (buffer.length > MAX_MESSAGES) buffer.length = MAX_MESSAGES;
  if (isSubscribed(entry.uno)) {
    watched = [entry, ...watched];
    if (watched.length > MAX_WATCHED) watched.length = MAX_WATCHED;
  }
  notify();
}

// Start monitoring. Ref-counted so two mounted panes share one subscription and
// the last one to leave tears it down.
export function startMonitor() {
  refCount += 1;
  if (refCount > 1) return;

  connectMqtt();
  WILDCARDS.forEach((w) => subscribeTopic(w));
  // The raw log shows EVERYTHING, the device's "Device Offline" last will included.
  detach = addMessageListener((topic, payload) => record(topic, payload), { offlineNotices: true });
}

export function stopMonitor() {
  refCount = Math.max(0, refCount - 1);
  if (refCount > 0) return;
  if (detach) { detach(); detach = null; }
  if (flushTimer) { clearInterval(flushTimer); flushTimer = null; }
  WILDCARDS.forEach((w) => unsubscribeTopic(w));
}

/** fn(buffer, watched) — both newest first. */
export function subscribeMessages(fn) {
  listeners.add(fn);
  fn(buffer, watched);
  return () => listeners.delete(fn);
}

export function getMessages() {
  return buffer;
}

export function clearMessages() {
  buffer = [];
  watched = [];
  notify();
}

/**
 * The Filtered pane's lines: everything for `unos` in either ring, newest
 * first, each once. `buffer` still contributes — it holds a number's traffic
 * from BEFORE it was watched — and `watched` keeps it after `buffer` has
 * rolled past. Lines below `floor` (the pane's last Clear) are left out.
 */
export function watchedLines(all, kept, unos, floor = 0) {
  const byId = new Map();
  [all, kept].forEach((list) => list.forEach((m) => {
    if (m.id > floor && unos.has(m.uno)) byId.set(m.id, m);
  }));
  return [...byId.values()].sort((a, b) => b.id - a.id);
}

// Log a line the console itself generated (a publish, a subscription), so the
// view shows what WE sent alongside what arrived — the reference console does
// the same and it is what makes a send/response pair readable.
export function logLocal(text) {
  record('app', text);
}
